import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CaretLeft, ChartBar } from '@phosphor-icons/react';
import { useAuth } from '../../../App';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { getEconomicsClientsDetail } from '../api';
import { money, StatCard, Panel, MarginCell, SuperAdminGate, EconomicsSourceNote } from './economicsShared';

function EconomicsClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      setLoading(false);
      return;
    }
    setLoading(true);
    getEconomicsClientsDetail()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.role]);

  const summary = data?.summary || {};
  const currency = data?.currency || 'INR';
  const clients = useMemo(() => {
    const rows = data?.clients || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) =>
      [c.client_name, c.product_name, c.client_id, c.product_id]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle)));
  }, [data?.clients, q]);

  return (
    <div data-testid="economics-clients-page">
      <SuperAdminGate user={user}>
        <div style={{ marginBottom: '12px' }}>
          <Link
            to="/dashboard"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6B7280', textDecoration: 'none' }}
          >
            <CaretLeft size={14} /> Overview
          </Link>
          <h2 style={{ margin: '6px 0 2px', fontSize: '16px', fontWeight: 700 }}>Client revenue vs spend</h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            Every client’s billing vs allocated people, infrastructure, and device costs. Open a row for the full breakdown.
          </p>
          <EconomicsSourceNote />
        </div>

        {loading ? <SkeletonBlock rows={6} /> : error ? (
          <EmptyState icon={ChartBar} title="Could not load" description={String(error)} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <StatCard label="Clients" value={summary.client_count || 0} sub={`${summary.clients_with_billing || 0} with billing`} color="#0EA5E9" />
              <StatCard label="Revenue / mo" value={money(summary.revenue_monthly, currency)} color="#059669" />
              <StatCard label="Spend / mo" value={money(summary.spend_monthly, currency)} color="#DC2626" />
              <StatCard label="Margin / mo" value={money(summary.margin_monthly, currency)} sub={`${summary.margin_pct || 0}%`} color={(summary.margin_monthly || 0) >= 0 ? '#002FA7' : '#B91C1C'} />
            </div>

            <Panel
              title="All clients"
              actions={(
                <input
                  className="input-field"
                  placeholder="Search client or product…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ padding: '6px 10px', fontSize: '12px', minWidth: '200px' }}
                />
              )}
            >
              {clients.length === 0 ? (
                <EmptyState icon={ChartBar} title="No clients found" description="Adjust search, or set up clients under Companies." />
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Product</th>
                        <th>Revenue / mo</th>
                        <th>Spend / mo</th>
                        <th>People</th>
                        <th>Infra</th>
                        <th>Devices</th>
                        <th>Members</th>
                        <th>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((c) => (
                        <tr
                          key={c.client_id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/dashboard/assets/economics/clients/${c.client_id}`)}
                        >
                          <td style={{ fontWeight: 600 }}>{c.client_name}</td>
                          <td>{c.product_name || '—'}</td>
                          <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(c.revenue_monthly, currency)}</td>
                          <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(c.spend_monthly, currency)}</td>
                          <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(c.spend_people_monthly, currency)}</td>
                          <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(c.spend_infra_monthly, currency)}</td>
                          <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(c.spend_device_monthly, currency)}</td>
                          <td>{c.member_count}</td>
                          <td><MarginCell value={c.margin_monthly} pct={c.margin_pct} alwaysShow /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </>
        )}
      </SuperAdminGate>
    </div>
  );
}

export default EconomicsClientsPage;
